import {
  Archive,
  ArrowLeftRight,
  Banknote,
  BookOpen,
  BookText,
  Boxes,
  Building2,
  CalendarRange,
  ClipboardCheck,
  Clock,
  Contact,
  FileBarChart,
  FileMinus,
  FileSpreadsheet,
  FileUp,
  FileText,
  Gauge,
  HandCoins,
  Hash,
  IdCard,
  KeySquare,
  Landmark,
  LayoutDashboard,
  LayoutTemplate,
  ListTree,
  Package,
  Percent,
  PiggyBank,
  Receipt,
  ReceiptText,
  RotateCcw,
  Scale,
  ScrollText,
  Settings2,
  ShieldCheck,
  Store,
  Tags,
  TrendingUp,
  Undo2,
  Upload,
  UserCheck,
  UserCog,
  Vault,
  Wallet,
  Wrench,
} from 'lucide-react';
import type { AppConfig } from '@shared/app/app-config';
import type { NavChildItem, NavGroupChild } from '@shared/components/layout/nav-config';
import {
  PERMISSIONS,
  ROLES_MANAGEMENT_PERMISSIONS,
  crudPermissions,
} from '@shared/constants/permissions';
import { STORAGE_KEYS } from '@shared/constants/storage-keys';
import { VENDOR_AREA, VENDOR_SECTION } from '@/modules/finance/vendor-area';
import { PATHS } from '@/router/paths';

/**
 * Finance navigation. Every destination is gated on the key(s) its route
 * guard and its backend endpoint require — never a role name — so a link is
 * offered exactly when its page will open.
 *
 * Finance & Accounts screens need `finance.access` (and `finance.view`
 * wherever money is shown). These are granted per employee and the Owner
 * bypass does not cover them; the OWNER role itself carries `finance.access`
 * only. Billing, master data and access administration use ordinary role
 * permissions.
 */
const FINANCE_VIEW_KEYS = [PERMISSIONS.finance.access, PERMISSIONS.finance.view] as const;

const financeChildren: NavGroupChild[] = [
  {
    label: 'Overview',
    to: PATHS.app.financeOverview,
    icon: LayoutDashboard,
    allOf: FINANCE_VIEW_KEYS,
  },
  {
    // Ledger figures: cash, owed, profit and what falls due — per currency.
    label: 'Dashboard',
    to: PATHS.app.financeDashboard,
    icon: Gauge,
    allOf: FINANCE_VIEW_KEYS,
  },
  {
    // The vendor directory: Vendor Master and Services & Subscriptions are
    // its tabs (VendorAreaTabs). It opens on Vendor Master, which needs
    // `finance.access` only; Services & Subscriptions shows prices, so it
    // also needs `finance.view`.
    label: VENDOR_AREA,
    to: PATHS.app.financeVendors,
    icon: Store,
    permission: PERMISSIONS.finance.access,
    activePrefixes: [PATHS.app.financeVendors, PATHS.app.financeServices],
  },
  {
    // What the companies owe their vendors, kept apart from customer
    // receivables (Billing → Receivables). Supplier Bills and the Payment
    // Register keep their addresses under /finance/vendor-management.
    type: 'subgroup',
    label: 'Accounts Payable',
    icon: Wallet,
    matchPrefixes: ['/finance/payables', PATHS.app.financeExpenses, PATHS.app.financePayments],
    children: [
      {
        label: VENDOR_SECTION.payables,
        to: PATHS.app.financeExpenses,
        icon: ReceiptText,
        allOf: FINANCE_VIEW_KEYS,
      },
      {
        label: VENDOR_SECTION.ledger,
        to: PATHS.app.financePayments,
        icon: ScrollText,
        allOf: FINANCE_VIEW_KEYS,
      },
      {
        label: 'Vendor Payments',
        to: PATHS.app.financeVendorPayments,
        icon: Banknote,
        allOf: FINANCE_VIEW_KEYS,
      },
      {
        label: 'Vendor Credits',
        to: PATHS.app.financeVendorCredits,
        icon: FileMinus,
        allOf: FINANCE_VIEW_KEYS,
      },
      {
        label: 'Vendor Refunds',
        to: PATHS.app.financeVendorRefunds,
        icon: Undo2,
        allOf: FINANCE_VIEW_KEYS,
      },
      {
        label: 'Vendor Ledger',
        to: PATHS.app.financeVendorLedger,
        icon: BookOpen,
        allOf: FINANCE_VIEW_KEYS,
      },
      {
        label: 'Payables Aging',
        to: PATHS.app.financeApAging,
        icon: Clock,
        allOf: FINANCE_VIEW_KEYS,
      },
    ],
  },
  {
    label: 'Reimbursements',
    to: PATHS.app.financeReimbursements,
    icon: HandCoins,
    allOf: FINANCE_VIEW_KEYS,
  },
  {
    type: 'subgroup',
    label: 'Asset Management',
    icon: Boxes,
    matchPrefixes: ['/finance/asset-management'],
    children: [
      {
        label: 'Assets',
        to: PATHS.app.financeAssets,
        icon: Package,
        permission: PERMISSIONS.finance.access,
        activePrefixes: [PATHS.app.financeAssetOverview],
      },
      {
        label: 'Assignments',
        to: PATHS.app.financeAssetAssignments,
        icon: UserCheck,
        permission: PERMISSIONS.finance.access,
      },
      {
        label: 'Repairs & Maintenance',
        to: PATHS.app.financeAssetRepairs,
        icon: Wrench,
        permission: PERMISSIONS.finance.access,
      },
      {
        label: 'Disposal',
        to: PATHS.app.financeAssetDisposals,
        icon: Archive,
        permission: PERMISSIONS.finance.access,
      },
    ],
  },
];

/**
 * Accounting — the ledger and its reports. Every screen reads with
 * `finance.access` + `finance.view`; set-up, posting and approval actions
 * re-check `finance.manage` / `finance.approve` on the screen (and the API).
 */
const accountingChildren: NavGroupChild[] = [
  {
    label: 'Setup & Settings',
    to: PATHS.app.accountingSetup,
    icon: Settings2,
    allOf: FINANCE_VIEW_KEYS,
  },
  {
    label: 'Chart of Accounts',
    to: PATHS.app.accountingAccounts,
    icon: ListTree,
    allOf: FINANCE_VIEW_KEYS,
  },
  {
    label: 'Journal',
    to: PATHS.app.accountingJournal,
    icon: BookText,
    allOf: FINANCE_VIEW_KEYS,
  },
  {
    label: 'General Ledger',
    to: PATHS.app.accountingGeneralLedger,
    icon: BookOpen,
    allOf: FINANCE_VIEW_KEYS,
  },
  {
    label: 'Accounting Periods',
    to: PATHS.app.accountingPeriods,
    icon: CalendarRange,
    allOf: FINANCE_VIEW_KEYS,
  },
  {
    label: 'Opening Balances',
    to: PATHS.app.accountingOpeningBalances,
    icon: Scale,
    allOf: FINANCE_VIEW_KEYS,
  },
  {
    type: 'subgroup',
    label: 'Financial Reports',
    icon: FileBarChart,
    matchPrefixes: ['/finance/accounting/reports'],
    children: [
      {
        label: 'Profit & Loss',
        to: PATHS.app.accountingProfitLoss,
        icon: TrendingUp,
        allOf: FINANCE_VIEW_KEYS,
      },
      {
        label: 'Balance Sheet',
        to: PATHS.app.accountingBalanceSheet,
        icon: Scale,
        allOf: FINANCE_VIEW_KEYS,
      },
      {
        label: 'Cash Flow',
        to: PATHS.app.accountingCashFlow,
        icon: ArrowLeftRight,
        allOf: FINANCE_VIEW_KEYS,
      },
      {
        label: 'Trial Balance',
        to: PATHS.app.accountingTrialBalance,
        icon: FileSpreadsheet,
        allOf: FINANCE_VIEW_KEYS,
      },
      {
        // Output and input tax of a period, from the tax accounts' postings.
        label: 'Tax Summary',
        to: PATHS.app.accountingTaxSummary,
        icon: Percent,
        allOf: FINANCE_VIEW_KEYS,
      },
      {
        label: 'Control Reconciliation',
        to: PATHS.app.accountingControlReconciliation,
        icon: ClipboardCheck,
        allOf: FINANCE_VIEW_KEYS,
      },
    ],
  },
  {
    label: 'Imports',
    to: PATHS.app.accountingImports,
    icon: Upload,
    allOf: FINANCE_VIEW_KEYS,
  },
];

/** Treasury — bank and cash, statements, transfers, loans and investments. */
const treasuryChildren: NavGroupChild[] = [
  {
    label: 'Bank & Cash Position',
    to: PATHS.app.treasuryCashPosition,
    icon: Vault,
    allOf: FINANCE_VIEW_KEYS,
  },
  {
    label: 'Bank Reconciliation',
    to: PATHS.app.treasuryReconciliation,
    icon: ClipboardCheck,
    allOf: FINANCE_VIEW_KEYS,
  },
  {
    // Imported statement files, matched line by line to the ledger.
    label: 'Bank Statements',
    to: PATHS.app.treasuryStatements,
    icon: FileUp,
    allOf: FINANCE_VIEW_KEYS,
  },
  {
    label: 'Transfers',
    to: PATHS.app.treasuryTransfers,
    icon: ArrowLeftRight,
    allOf: FINANCE_VIEW_KEYS,
  },
  {
    label: 'Loans',
    to: PATHS.app.treasuryLoans,
    icon: Landmark,
    allOf: FINANCE_VIEW_KEYS,
  },
  {
    label: 'Investments',
    to: PATHS.app.treasuryInvestments,
    icon: PiggyBank,
    allOf: FINANCE_VIEW_KEYS,
  },
];

/**
 * Receivables — money owed to the companies. Receipts and refunds carry
 * `invoice-payments.view`, credit notes `invoices.view` (the keys their
 * endpoints require); the aging and the client statements read the ledger,
 * so they need `finance.access` + `finance.view`.
 */
const receivablesChildren: NavChildItem[] = [
  {
    label: 'Customer Receipts',
    to: PATHS.app.receipts,
    icon: Receipt,
    permission: PERMISSIONS.invoicePayments.view,
  },
  {
    label: 'Customer Refunds',
    to: PATHS.app.customerRefunds,
    icon: RotateCcw,
    permission: PERMISSIONS.invoicePayments.view,
  },
  {
    label: 'Credit Notes',
    to: PATHS.app.creditNotes,
    icon: FileMinus,
    permission: PERMISSIONS.invoices.view,
  },
  {
    label: 'Receivables Aging',
    to: PATHS.app.arAging,
    icon: Clock,
    allOf: FINANCE_VIEW_KEYS,
  },
  {
    label: 'Customer Ledger',
    to: PATHS.app.customerLedger,
    icon: BookOpen,
    allOf: FINANCE_VIEW_KEYS,
  },
];

/**
 * Invoicing. Each entry carries the key its route requires: `invoices.view`
 * for the directory and the separate catalogue keys for the setup screens, so
 * a role that raises invoices without editing taxes sees only the list.
 */
const invoicingChildren: NavChildItem[] = [
  {
    label: 'Invoices',
    to: PATHS.app.invoices,
    icon: FileText,
    permission: PERMISSIONS.invoices.view,
  },
  {
    label: 'Items & Services',
    to: PATHS.app.invoiceItems,
    icon: Package,
    permission: PERMISSIONS.invoiceItems.view,
  },
  {
    label: 'Taxes',
    to: PATHS.app.invoiceTaxes,
    icon: Percent,
    permission: PERMISSIONS.invoiceTaxes.view,
  },
  {
    label: 'Templates',
    to: PATHS.app.invoiceTemplates,
    icon: LayoutTemplate,
    permission: PERMISSIONS.invoiceTemplates.view,
  },
  {
    label: 'Bill Numbering',
    to: PATHS.app.invoiceNumbering,
    icon: Hash,
    permission: PERMISSIONS.invoiceNumbering.view,
  },
];

export const financeConfig: AppConfig = {
  application: 'FINANCE',
  name: 'Finance',
  tagline: 'Accounting, treasury, vendors, assets, billing and access administration.',
  usesPermissions: true,
  navigation: [
    {
      heading: 'Finance & Accounts',
      items: [
        {
          type: 'group',
          label: 'Finance & Accounts',
          icon: Landmark,
          storageKey: STORAGE_KEYS.FINANCE_SIDEBAR,
          // Not all of `/finance`: Accounting and Treasury are groups of their own.
          matchPrefix: [
            '/finance/overview',
            '/finance/dashboard',
            '/finance/vendor-management',
            '/finance/payables',
            '/finance/reimbursements',
            '/finance/asset-management',
          ],
          children: financeChildren,
        },
      ],
    },
    {
      heading: 'Accounting',
      items: [
        {
          type: 'group',
          label: 'Accounting',
          icon: BookText,
          storageKey: STORAGE_KEYS.ACCOUNTING_SIDEBAR,
          matchPrefix: '/finance/accounting',
          children: accountingChildren,
        },
        {
          type: 'group',
          label: 'Treasury',
          icon: Vault,
          storageKey: STORAGE_KEYS.TREASURY_SIDEBAR,
          matchPrefix: '/finance/treasury',
          children: treasuryChildren,
        },
      ],
    },
    {
      heading: 'Billing',
      items: [
        {
          type: 'link',
          label: 'Clients',
          to: PATHS.app.clients,
          icon: Contact,
          permission: PERMISSIONS.clients.view,
        },
        {
          type: 'group',
          label: 'Invoicing',
          icon: ReceiptText,
          storageKey: STORAGE_KEYS.INVOICING_SIDEBAR,
          matchPrefix: '/invoicing',
          children: invoicingChildren,
        },
        {
          type: 'group',
          label: 'Receivables',
          icon: HandCoins,
          storageKey: STORAGE_KEYS.RECEIVABLES_SIDEBAR,
          matchPrefix: '/receivables',
          children: receivablesChildren,
        },
      ],
    },
    {
      heading: 'Master data',
      items: [
        {
          type: 'link',
          label: 'Companies',
          to: PATHS.app.companies,
          icon: Building2,
          permission: PERMISSIONS.companies.view,
        },
        {
          // People / HRMS records; the screens also need the HRMS application.
          type: 'link',
          label: 'Employees',
          to: PATHS.app.employees,
          icon: IdCard,
          permission: PERMISSIONS.employees.view,
        },
        {
          type: 'link',
          label: 'Vendor Categories',
          to: PATHS.app.vendorCategories,
          icon: Tags,
          permission: crudPermissions('vendor-categories').view,
        },
        {
          type: 'link',
          label: 'Asset Categories',
          to: PATHS.app.assetCategories,
          icon: Boxes,
          permission: crudPermissions('asset-categories').view,
        },
      ],
    },
    {
      heading: 'Access',
      items: [
        {
          type: 'link',
          label: 'Users',
          to: PATHS.app.accessUsers,
          icon: UserCog,
          permission: PERMISSIONS.users.view,
        },
        {
          // `roles.view` alone is held by everyone for role pickers.
          type: 'link',
          label: 'Roles',
          to: PATHS.app.roles,
          icon: ShieldCheck,
          anyOf: ROLES_MANAGEMENT_PERMISSIONS,
        },
        {
          type: 'link',
          label: 'Permissions',
          to: PATHS.app.permissions,
          icon: KeySquare,
          permission: PERMISSIONS.permissions.view,
        },
        {
          type: 'link',
          label: 'Audit Log',
          to: PATHS.app.auditLogs,
          icon: ScrollText,
          permission: PERMISSIONS.auditLogs.view,
        },
      ],
    },
  ],
};
